import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Star, AlertTriangle, Search, X, Layers } from 'lucide-react';
import { CatalogLinkDialog } from '../content-items/CatalogLinkDialog';
import { ContentItemLinkDialog } from '../content-items/ContentItemLinkDialog';
import { POLineItemSelect } from '../purchase-orders/POLineItemSelect';
import { EntityCustomFields } from '../shared/EntityCustomFields';
import { useAuth } from '../../context/AuthContext';

const SESSION_TYPES = [
  { value: 'in_person', label: 'In-Person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'hybrid', label: 'Hybrid' },
];

export const SessionFormDialog = ({
  open, onOpenChange, editingSession, sessionForm, setSessionForm, trainers,
  recommendations, conflictWarning, availablePos, categories = [],
  onFetchRecommendations, onCheckConflict, onSelectRecommendedTrainer, onSave,
}) => {
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [contentItemDialogOpen, setContentItemDialogOpen] = useState(false);
  const { user } = useAuth();
  const handleCatalogSelect = (course) => {
    setCatalogDialogOpen(false);
    if (!course) return;
    setSessionForm(f => ({ ...f, course_id: course.course_id, course_title: course.title }));
    onFetchRecommendations(course.course_id, course.title, sessionForm.session_type);
  };

  const handleContentItemSelect = (item) => {
    setContentItemDialogOpen(false);
    setSessionForm(f => ({ ...f, content_item_id: item?.id || '', content_item_title: item?.title || '' }));
  };

  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{editingSession ? 'Edit Session' : 'Add New Session'}</DialogTitle>
        <DialogDescription>Each session covers one course and can be single or multi-day</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Session Title *</Label>
          <Input
            value={sessionForm.title}
            onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
            placeholder="e.g., Day 1 - Azure Fundamentals"
          />
        </div>

        <div className="space-y-2">
          <Label>Course</Label>
          {sessionForm.course_id ? (
            <div className="flex items-center justify-between gap-2 border rounded-lg p-2.5 bg-lime/5 border-lime/30" data-testid="session-selected-catalog-course">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{sessionForm.course_title}</p>
                <p className="text-xs text-muted-foreground">From Partner Skilling Catalog</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={() => setCatalogDialogOpen(true)} data-testid="change-catalog-course-btn">Change</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setSessionForm(f => ({ ...f, course_id: '', course_title: '' }))} data-testid="clear-catalog-course-btn">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Button type="button" variant="outline" className="w-full justify-start" onClick={() => setCatalogDialogOpen(true)} data-testid="open-catalog-search-btn">
                <Search className="w-4 h-4 mr-2" />Select from Catalog...
              </Button>
              <p className="text-xs text-muted-foreground">Or enter a custom course title below</p>
              <Input
                value={sessionForm.course_title}
                onChange={(e) => setSessionForm({ ...sessionForm, course_title: e.target.value, course_id: '' })}
                placeholder="Custom course title"
              />
            </>
          )}
        </div>

        <div className="space-y-2">
          <Label>Linked Content Dev Course</Label>
          {sessionForm.content_item_id ? (
            <div className="flex items-center justify-between gap-2 border rounded-lg p-2.5 bg-blue-50 border-blue-200" data-testid="session-selected-content-item">
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{sessionForm.content_item_title}</p>
                <p className="text-xs text-muted-foreground">Content Dev tracker will flag urgency if this course isn't build-complete yet</p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={() => setContentItemDialogOpen(true)} data-testid="change-content-item-btn">Change</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setSessionForm(f => ({ ...f, content_item_id: '', content_item_title: '' }))} data-testid="clear-content-item-btn">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ) : (
            <Button type="button" variant="outline" className="w-full justify-start" onClick={() => setContentItemDialogOpen(true)} data-testid="open-content-item-search-btn">
              <Layers className="w-4 h-4 mr-2" />Link to a Content Dev course (optional)...
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            checked={sessionForm.is_multi_day}
            onCheckedChange={(checked) => setSessionForm({ ...sessionForm, is_multi_day: checked })}
          />
          <Label>Multi-day session</Label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start Date *</Label>
            <Input
              type="date"
              value={sessionForm.session_date}
              onChange={(e) => {
                const d = e.target.value;
                setSessionForm(f => ({ ...f, session_date: d }));
                onCheckConflict(sessionForm.trainer_id, d, sessionForm.start_time, sessionForm.end_time, editingSession?.id);
              }}
            />
          </div>
          {sessionForm.is_multi_day && (
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={sessionForm.end_date}
                onChange={(e) => setSessionForm({ ...sessionForm, end_date: e.target.value })}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start Time</Label>
            <Input
              type="time"
              value={sessionForm.start_time}
              onChange={(e) => setSessionForm({ ...sessionForm, start_time: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>End Time</Label>
            <Input
              type="time"
              value={sessionForm.end_time}
              onChange={(e) => setSessionForm({ ...sessionForm, end_time: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Session Type</Label>
          <Select
            value={sessionForm.session_type}
            onValueChange={(v) => setSessionForm({ ...sessionForm, session_type: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SESSION_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {sessionForm.session_type === 'virtual' || sessionForm.session_type === 'hybrid' ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Platform</Label>
              <Input
                value={sessionForm.platform}
                onChange={(e) => setSessionForm({ ...sessionForm, platform: e.target.value })}
                placeholder="Teams, Zoom, etc."
              />
            </div>
            <div className="space-y-2">
              <Label>Meeting Link</Label>
              <Input
                value={sessionForm.meeting_link}
                onChange={(e) => setSessionForm({ ...sessionForm, meeting_link: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
        ) : null}

        {sessionForm.session_type === 'in_person' || sessionForm.session_type === 'hybrid' ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Venue</Label>
              <Input
                value={sessionForm.venue}
                onChange={(e) => setSessionForm({ ...sessionForm, venue: e.target.value })}
                placeholder="Venue name"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={sessionForm.address}
                onChange={(e) => setSessionForm({ ...sessionForm, address: e.target.value })}
                placeholder="Full address"
              />
            </div>
          </div>
        ) : null}

        {/* ── Trainer Assignment ─────────────────────────────────── */}
        <div className="border rounded-lg p-4 space-y-3 bg-muted/20">
          <h4 className="text-sm font-semibold">Trainer Assignment</h4>

          {/* Recommendations chips */}
          {recommendations.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-500" />
                Recommended based on this course:
              </p>
              <div className="flex flex-wrap gap-2">
                {recommendations.slice(0, 4).map(rec => (
                  <button
                    key={rec.trainer.id}
                    type="button"
                    onClick={() => onSelectRecommendedTrainer(rec)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-all ${
                      sessionForm.trainer_id === rec.trainer.id
                        ? 'bg-lime border-lime text-lime-foreground'
                        : 'bg-white border-violet-200 text-violet-700 hover:border-violet-400'
                    }`}
                  >
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-400" />
                    {rec.trainer.name}
                    {rec.avg_rating && <span>({rec.avg_rating})</span>}
                    {rec.trainer.country && <span className="text-muted-foreground">· {rec.trainer.country}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Trainer picker from roster */}
          {trainers.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Trainer Assignment <span className="font-normal text-muted-foreground">(from roster, up to 5)</span></Label>
                {sessionForm.session_trainers.length < 5 && (
                  <button
                    type="button"
                    onClick={() => setSessionForm(f => ({ ...f, session_trainers: [...f.session_trainers, { trainer_id: '', name: '', email: '' }] }))}
                    className="text-xs text-lime-700 hover:text-lime-900 font-medium"
                    data-testid="add-trainer-slot"
                  >
                    + Add Trainer
                  </button>
                )}
              </div>

              {sessionForm.session_trainers.map((slot, idx) => {
                const isManual = !slot.trainer_id;
                return (
                <div key={idx} className="space-y-1.5" data-testid={`trainer-slot-${idx}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Select
                        value={slot.trainer_id || '__manual__'}
                        onValueChange={(v) => {
                          const updated = [...sessionForm.session_trainers];
                          if (v === '__manual__') {
                            updated[idx] = { trainer_id: '', name: slot.name || '', email: slot.email || '' };
                          } else {
                            const t = trainers.find(tr => tr.id === v);
                            if (t) updated[idx] = { trainer_id: t.id, name: t.name, email: t.email || '' };
                          }
                          setSessionForm(f => ({ ...f, session_trainers: updated }));
                        }}
                      >
                        <SelectTrigger className="h-8 text-sm" data-testid={`trainer-select-${idx}`}>
                          <SelectValue placeholder={`Select Trainer ${idx + 1}...`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__manual__">— Not in roster (enter manually) —</SelectItem>
                          {trainers.map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}{t.country ? ` (${t.country})` : ''}{t.avg_rating ? ` ⭐${t.avg_rating}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {!isManual && slot.email && (
                      <span className="text-xs text-muted-foreground truncate max-w-[120px]">{slot.email}</span>
                    )}
                    {idx > 0 && (
                      <button
                        type="button"
                        onClick={() => setSessionForm(f => ({ ...f, session_trainers: f.session_trainers.filter((_, i) => i !== idx) }))}
                        className="text-red-400 hover:text-red-600 text-xs"
                        data-testid={`remove-trainer-${idx}`}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {isManual && (
                    <div className="grid grid-cols-2 gap-2 pl-1">
                      <Input
                        value={slot.name}
                        onChange={(e) => {
                          const updated = [...sessionForm.session_trainers];
                          updated[idx] = { ...updated[idx], name: e.target.value };
                          setSessionForm(f => ({ ...f, session_trainers: updated }));
                        }}
                        placeholder="Trainer name"
                        className="h-8 text-sm"
                        data-testid={`trainer-manual-name-${idx}`}
                      />
                      <Input
                        value={slot.email}
                        onChange={(e) => {
                          const updated = [...sessionForm.session_trainers];
                          updated[idx] = { ...updated[idx], email: e.target.value };
                          setSessionForm(f => ({ ...f, session_trainers: updated }));
                        }}
                        placeholder="Trainer email (optional)"
                        className="h-8 text-sm"
                        data-testid={`trainer-manual-email-${idx}`}
                      />
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {/* Conflict warning */}
          {conflictWarning?.has_conflict && (
            <div className="flex items-start gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-red-700">
                <p className="font-semibold">Scheduling Conflict</p>
                <p>
                  {conflictWarning.trainer_name} is already assigned to
                  "{conflictWarning.conflicts[0]?.session_title}"
                  on this date ({conflictWarning.conflicts[0]?.start_time}–{conflictWarning.conflicts[0]?.end_time})
                </p>
              </div>
            </div>
          )}

          {/* Contract fields */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs">Contract ID</Label>
              <Input
                value={sessionForm.contract_id}
                onChange={(e) => setSessionForm(f => ({ ...f, contract_id: e.target.value }))}
                placeholder="e.g. TF-2024-001"
                data-testid="contract-id-input"
              />
            </div>
            <div className="flex items-end pb-1 gap-2">
              <input
                type="checkbox"
                id="contract_issued"
                checked={sessionForm.contract_issued}
                onChange={(e) => setSessionForm(f => ({ ...f, contract_issued: e.target.checked }))}
                className="w-4 h-4 rounded accent-lime"
                data-testid="contract-issued-checkbox"
              />
              <label htmlFor="contract_issued" className="text-sm cursor-pointer select-none">
                Contract Issued
              </label>
            </div>
          </div>
          {sessionForm.trainer_name && sessionForm.session_date && !sessionForm.contract_issued && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Trainer assigned but contract not yet issued
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea
            value={sessionForm.notes}
            onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })}
            placeholder="Additional notes..."
            rows={2}
          />
        </div>

        {/* Financials */}
        <div className="border rounded-lg p-3 space-y-3 bg-muted/20">
          <h4 className="text-sm font-semibold">Financials</h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Approved Price (USD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={sessionForm.session_cost}
                  onChange={(e) => setSessionForm(f => ({ ...f, session_cost: e.target.value }))}
                  placeholder="0.00"
                  className="pl-7"
                  data-testid="session-cost-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Amount Invoiced (USD)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={sessionForm.amount_invoiced}
                  onChange={(e) => setSessionForm(f => ({ ...f, amount_invoiced: e.target.value }))}
                  placeholder="0.00"
                  className="pl-7"
                  data-testid="session-amount-invoiced-input"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">PO# Invoiced in</Label>
              {availablePos.length > 0 ? (
                <Select
                  value={sessionForm.session_po_number || "__none__"}
                  onValueChange={(v) => setSessionForm(f => ({
                    ...f,
                    session_po_number: v === "__none__" ? "" : v,
                    session_po_line_item_id: '', session_po_line_item_description: '',
                  }))}
                >
                  <SelectTrigger data-testid="session-po-select">
                    <SelectValue placeholder="Link to PO..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No PO</SelectItem>
                    {availablePos.map(po => (
                      <SelectItem key={po.id} value={po.po_number}>
                        {po.po_number} (${(po.available_balance || 0).toLocaleString()} avail.){po.status === 'exhausted' ? ' — Exhausted, needs extension' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={sessionForm.session_po_number}
                  onChange={(e) => setSessionForm(f => ({ ...f, session_po_number: e.target.value }))}
                  placeholder="e.g. PO-2025-001"
                  data-testid="session-po-input"
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Invoice Month</Label>
              <Input
                type="month"
                value={sessionForm.invoiced_month || ''}
                onChange={(e) => setSessionForm(f => ({ ...f, invoiced_month: e.target.value }))}
                data-testid="session-invoiced-month-input"
              />
            </div>
          </div>
          <POLineItemSelect
            pos={availablePos}
            poNumber={sessionForm.session_po_number}
            lineItemId={sessionForm.session_po_line_item_id}
            deliveryType="session"
            deliveryDate={sessionForm.session_date}
            invoiceAmount={sessionForm.amount_invoiced || sessionForm.session_cost}
            onChange={(id, description) => setSessionForm(f => ({ ...f, session_po_line_item_id: id, session_po_line_item_description: description }))}
            testIdPrefix="session-po-line-item"
          />
        </div>

        {/* Custom Taxonomy Fields (schema-level, Super-Admin managed) */}
        <div className="pt-2 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground mb-3">Additional Taxonomy Fields</p>
          <EntityCustomFields
            entityType="session"
            values={sessionForm.custom_fields || {}}
            onChange={(v) => setSessionForm(f => ({ ...f, custom_fields: v }))}
            isSuperAdmin={user?.role === 'superadmin'}
            currentUser={user}
            testPrefix="session-custom-field"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} className="bg-lime text-lime-foreground hover:bg-lime/90">
            {editingSession ? 'Update Session' : 'Add Session'}
          </Button>
        </div>
      </div>
    </DialogContent>
    <CatalogLinkDialog
      open={catalogDialogOpen}
      onOpenChange={setCatalogDialogOpen}
      onSelect={handleCatalogSelect}
      showSkip
      title="Search Partner Skilling Catalog"
      description="Search for the course to deliver in this session."
    />
    <ContentItemLinkDialog
      open={contentItemDialogOpen}
      onOpenChange={setContentItemDialogOpen}
      onSelect={handleContentItemSelect}
    />
  </Dialog>
  );
};

export default SessionFormDialog;
